import { Theme } from '@mui/material';

import { MarksAnalytics, StudentAnalytics } from '../../services/students/types';
import { ANALYTICS_QUESTION_TYPES, MISTAKE_TYPES } from '../../utils/common/constants';

import {
  AnalyticsData, BaseChartData, SortableMarkType,
} from './types';

// Use the MISTAKE_LABELS object to get the mistake label
export const getMistakeLabel = (mistake: string) => {
  const mistakeLabel = MISTAKE_TYPES[mistake as keyof typeof MISTAKE_TYPES] ?? mistake;

  return mistakeLabel;
};

const getAnalyticsName = (name: string): string =>
  // Strip 'Analytics' from name and turn camelCase into Title Case
  name.slice(0, -9).replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

export const sortAnalyticsDataArray =
(analyticsDataArray: AnalyticsData[], sortBy: SortableMarkType): AnalyticsData[] => {
  // Order analyticsArray by descending sort type
  analyticsDataArray
    .sort((a, b) => b[sortBy] - a[sortBy]);

  // Only take the first 10 items
  return analyticsDataArray.slice(0, 10);
};

export const wilsonScoreInterval = (failures: number, total: number, z: number = 1.96): number => {
  const p = failures / total;
  const denominator = 1 + (z * z) / total;
  const numerator =
  p + (z * z) / (2 * total) - z * Math.sqrt(
    ((p * (1 - p)) / total) + (z * z) / (4 * total * total));
  return numerator / denominator;
};

const getMarksScores = (analytics: MarksAnalytics, name: string): AnalyticsData | null => {
  const { attemptedMarks, missedMarks, sillyMarks, inaccuracyMarks } = analytics;

  const incorrectMarks = missedMarks + sillyMarks + inaccuracyMarks;

  const errorScore = wilsonScoreInterval(incorrectMarks, attemptedMarks);
  const score = 1 - errorScore;

  const getErrorPercentage = (errorMarks: number): number => {
    if (incorrectMarks === 0) return 0;

    const errorPercentage = (errorMarks * errorScore * 100) / incorrectMarks;

    return errorPercentage;
  };

  return {
    name,
    attemptedMarks,
    correctMarks: score * 100,
    missedMarks: getErrorPercentage(missedMarks),
    sillyMarks: getErrorPercentage(sillyMarks),
    inaccuracyMarks: getErrorPercentage(inaccuracyMarks),
    incorrectMarks: errorScore * 100,
  };
};

export const getQuestionAnalyticsDataArray = (analyticsData: StudentAnalytics): AnalyticsData[] => {
  const questionsAnalyticsArray = ANALYTICS_QUESTION_TYPES.map((key) => {
    const analytics = analyticsData[key as keyof StudentAnalytics] as MarksAnalytics;

    if (!analytics?.attemptedMarks) {
      // If the analytics object is not initialised for this particular question
      return null;
    }

    return getMarksScores(analytics, getAnalyticsName(key));
  });

  const result = questionsAnalyticsArray.filter((x) => x !== null) as AnalyticsData[];

  return result;
};

export const getTopicsAnalyticsDataArray = (analyticsData: StudentAnalytics): AnalyticsData[] => {
  const topicsAnalyticsArray = analyticsData.topicsAnalytics?.map((topicAnalytics) => {
    if (!topicAnalytics?.analytics?.attemptedMarks) {
      // If the analytics object is not initialised for this particular topic
      return null;
    }

    return getMarksScores(topicAnalytics.analytics, topicAnalytics.topic ?? 'Unknown Topic');
  }) ?? [];

  const result = topicsAnalyticsArray.filter((x) => x !== null) as AnalyticsData[];

  return result;
};

export const getMistakesAnalyticsDataArray = (
  analyticsData: StudentAnalytics, theme: Theme): BaseChartData[] => {
  const overallAnalytics = analyticsData.analytics;

  const incorrectMarks =
    overallAnalytics.missedMarks + overallAnalytics.sillyMarks + overallAnalytics.inaccuracyMarks;

  const COLORS = [
    theme.palette.mistake.dark,
    theme.palette.mistake.main,
    theme.palette.mistake.light,
  ];

  // Create array of overall analytics and sort in ascending order of key
  const overallAnalyticsArray: [string, number][] = Object.entries(overallAnalytics);

  // Turn overall analytics into an array of objects {name: string, value: number}
  const mistakesAnalyticsArray: BaseChartData[] = overallAnalyticsArray
    .filter(([key]) => key !== 'attemptedMarks')
    .map(([key, value], index) => ({
      name: key,
      value: ((value * 100) / incorrectMarks) || 0,
      fill: COLORS[index % COLORS.length],
    }));

  return mistakesAnalyticsArray;
};

export const getAverageAnalyticsDataArray = (
  analyticsData: StudentAnalytics, theme: Theme): BaseChartData[] => {
  const overallAnalytics = analyticsData.analytics;

  const incorrectMarks =
    overallAnalytics.missedMarks + overallAnalytics.sillyMarks + overallAnalytics.inaccuracyMarks;

  const averageAnalyticsArray: BaseChartData[] = [{
    name: 'Correct',
    value: ((overallAnalytics.attemptedMarks
      - incorrectMarks) * 100) / overallAnalytics.attemptedMarks,
    fill: theme.palette.primary.dark,
  }, {
    name: 'Missed',
    value: (incorrectMarks * 100) / overallAnalytics.attemptedMarks,
    fill: theme.palette.backdrop.main,
  }];

  return averageAnalyticsArray;
};
